import { Modal } from "@shltelemedicine/shl-ui-lib";
import { useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useAuthContext } from "../../../../context/AuthContext";
import {
  ManageUsersActionType,
  useManageUsersContext,
} from "../../../../context/ManageUsersContext";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../../hooks/useAuthorizedAction";
import Role from "../../../../models/role";
import {
  IAddUser,
  UserDetailsFormPermissions,
} from "../../../../models/users.interface";
import { getProUserDefaultFormPermissions } from "../../../../utils/userUtil";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import UserIcon from "../../../Shared/UserIcon/UserIcon";
import UserInfoInputs from "../UserInfoInputs/UserInfoInputs";
import UserPermissionInputs from "../UserPermissionInputs/UserPermissionInputs";
import styles from "./AddUserModal.module.css";

type AddUserModalProps = {
  show?: boolean;
  onClose?: () => void;
  onSave?: (newUser: IAddUser) => Promise<boolean>;
};

const AddUserModal = ({ show, onClose, onSave }: AddUserModalProps) => {
  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const {
    state: { user },
  } = useAuthContext();
  const {
    state: {
      userOrgPermission: { defaults, loading },
    },
    dispatch,
  } = useManageUsersContext();
  const [saving, setSaving] = useState(false);

  const formMethods = useForm<IAddUser>({
    defaultValues: { sendActivationEmail: true },
  });
  const { handleSubmit, reset, watch, getValues } = formMethods;

  const isAdmin = watch("isAdmin"); //canManageProUsers");
  const entity = watch("entity");

  useEffect(() => {
    if (show === true) {
      const defaultProPermissions = getProUserDefaultFormPermissions(
        isAdmin ? "admin" : "pro",
        {
          isCroOrg,
          isCroExternOrg,
        }
      );
      const mergedPermissions: UserDetailsFormPermissions = {
        ...defaultProPermissions,
        ...defaults,
      };
      const formValues = getValues();

      reset({ ...formValues, ...mergedPermissions, emailVerification: true });
    }
  }, [show, reset, isCroOrg, isCroExternOrg, defaults, getValues, isAdmin]);

  useEffect(() => {
    const formValues = getValues();

    const defaultPermissions = getProUserDefaultFormPermissions(
      isAdmin ? "admin" : "pro",
      { isCroOrg, isCroExternOrg }
    );

    reset({ ...formValues, ...defaultPermissions });
  }, [isAdmin, reset, getValues, isCroOrg, isCroExternOrg]);

  useEffect(() => {
    dispatch({
      type: ManageUsersActionType.SET_ADD_USER_DETAILS,
      payload: {
        role: isAdmin ? Role.OrgAdmin : Role.OrgProfessional,
        entity: entity ?? user?.organization?.id,
      },
    });
  }, [isAdmin, entity, dispatch, user?.organization?.id]);

  const saveHandler = async (newUser: IAddUser) => {
    if (!onSave) return;
    setSaving(true);
    const success = await onSave(newUser);
    setSaving(false);
    success && reset({});
  };

  const closeHandler = () => {
    reset({});
    onClose && onClose();
  };

  return (
    <Modal
      dialogClassName={styles.dialog}
      centered
      backdrop="static"
      show={show}
      onHide={closeHandler}
      header={
        <div className="d-flex align-items-center">
          <UserIcon />
          <span className="mx-1"></span>
          <p className={`${styles.title} mb-0`}>
            <FormattedMessage
              id="MANAGEMENT.ADD_USER"
              defaultMessage="Add User"
            />
          </p>
        </div>
      }
      footer={
        <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-0">
          <CancelButton onClick={closeHandler} disabled={saving || loading} />
          <span className="mx-2"></span>
          <SaveButton
            onClick={handleSubmit(saveHandler)}
            className="prim-bg-gradient-color capitalized"
            disabled={saving || loading}
          >
            <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
            {saving && (
              <>
                &nbsp;&nbsp;
                <Spinner as="span" animation="border" size="sm" />
              </>
            )}
          </SaveButton>
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <Form className="py-4 px-4 mb-4">
          <Container fluid className="px-5">
            <UserInfoInputs entity={{ restricted: "pro" }} isModal />
            {/*TODO: uncomment when email verification flow is ready*/}
            {/* <CheckBoxFormInput<IAddUser>
              name={"emailVerification"}
              control={control}
              input={{
                disabled: true,
                className: "mt-3",
                label: (
                  <span className="font-md">
                    <FormattedMessage
                      id="MANAGEMENT.ADD_USER.NO_EMAIL_VERIFICATION"
                      defaultMessage="No email verification"
                    />
                  </span>
                ),
              }}
            /> */}
          </Container>
          <div className="border-bottom mx-4 my-3"></div>
          <Container fluid className="px-5">
            <UserPermissionInputs />
          </Container>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default AddUserModal;
